/**
 * Logo Selector - Compact Asset Manager Integration
 * Allows selecting logos from Asset Manager without routing away
 * Integrates Asset Management in a dialog for Image/Icon types only
 */

import { useEffect, useState } from 'react';
import { Upload, X, Edit2, AlertTriangle, Loader2 } from 'lucide-react';
import { Label } from '@/core/components/ui/label';
import { Button } from '@/core/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/core/components/ui/dialog';
import { useAssetManagementStore } from '@/features/asset-management/stores/asset-management-store';
import { AssetGrid } from '@/features/asset-management/components/organisms/AssetGrid';
import { AssetUploadDialog } from '@/features/asset-management/components/organisms/AssetUploadDialog';
import {
  Asset,
  AssetType,
} from '@/features/asset-management/types/asset-types';
import { useAuthenticatedImage } from '@/core/hooks/use-authenticated-image';

interface LogoSelectorProps {
  value: string | null | undefined;
  onChange: (url: string | null) => void;
}

export const LogoSelector = ({ value, onChange }: LogoSelectorProps) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [imageError, setImageError] = useState(false);

  // Use authenticated image hook to fetch S3 images with auth headers
  const { blobUrl, isLoading: isImageLoading } = useAuthenticatedImage(value);

  // Reset image error when value changes
  useEffect(() => {
    setImageError(false);
  }, [value]);

  // Asset management store state
  const {
    assets,
    isLoading,
    error,
    currentPage,
    pageSize,
    uploadDialogOpen,
    isUploading,
    uploadError,
    fetchAssets,
    setAssetTypeFilter,
    setCurrentPage,
    openUploadDialog,
    closeUploadDialog,
    setApplicationTypeFilter,
    uploadAsset,
    clearUploadError,
    reset: resetStore,
  } = useAssetManagementStore();

  // Initialize asset manager with filters when dialog opens
  useEffect(() => {
    if (isDialogOpen) {
      // Note: Asset filter only supports single type, so we set to null
      // and filter on client side to show Image and Icon types for logos
      setAssetTypeFilter(null);
      // Don't filter by application type - allow all
      setApplicationTypeFilter(null);
      // Fetch assets
      fetchAssets();
    }
  }, [isDialogOpen, setAssetTypeFilter, setApplicationTypeFilter, fetchAssets]);

  // Filter assets to only show Image and Icon types
  const filteredAssets = assets.filter(
    asset => asset.assetType === AssetType.Image || asset.assetType === AssetType.Icon
  );

  // Handle closing the dialog - reset the store filters
  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    // Reset store to clean state (this also resets filters)
    resetStore();
  };

  // Handle asset selection
  const handleSelectAsset = (asset: Asset) => {
    // Validate that the asset has a valid URL
    const url = asset.currentS3Url;
    if (!url || url.trim() === '') {
      console.warn('Selected asset has no valid S3 URL:', asset);
      return;
    }
    onChange(url);
    handleCloseDialog();
  };

  // Handle retry
  const handleRetry = () => {
    fetchAssets();
  };

  // Handle page change
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  return (
    <div>
      <Label className="text-xs font-medium text-gray-600 mb-2 block">
        Logo
      </Label>

      {!value ? (
        // No logo - Show select button
        <Button
          onClick={() => setIsDialogOpen(true)}
          variant="outline"
          className="w-full justify-center"
        >
          <Upload className="h-4 w-4 mr-2" />
          Select Logo
        </Button>
      ) : (
        // Logo exists - Show preview with edit button
        <div className="space-y-3">
          <div className="relative flex items-center gap-3 p-3 bg-gray-50 rounded-md border border-gray-300">
            {/* Logo Preview */}
            {isImageLoading ? (
              <div className="h-12 w-12 bg-gray-100 rounded flex items-center justify-center">
                <Loader2 className="h-5 w-5 text-gray-400 animate-spin" />
              </div>
            ) : !imageError && blobUrl ? (
              <img
                src={blobUrl}
                alt="Selected Logo"
                className="h-12 w-12 object-contain rounded"
                onError={() => setImageError(true)}
              />
            ) : (
              <div className="h-12 w-12 bg-gray-200 rounded flex items-center justify-center">
                <AlertTriangle className="h-5 w-5 text-gray-400" />
              </div>
            )}

            {/* Logo URL */}
            <div className="flex-1 min-w-0">
              <p className="text-xs text-gray-500 mb-1">Current Logo</p>
              <p className="text-sm text-gray-900 truncate">{value}</p>
            </div>

            {/* Clear Button */}
            <button
              onClick={() => {
                onChange(null);
                setImageError(false);
              }}
              className="flex-shrink-0 text-gray-400 hover:text-red-500 transition-colors"
              title="Remove logo"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Change Button */}
          <Button
            onClick={() => setIsDialogOpen(true)}
            variant="outline"
            className="w-full justify-center"
          >
            <Edit2 className="h-4 w-4 mr-2" />
            Change Logo
          </Button>
        </div>
      )}

      {/* Asset Manager Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Select Logo</DialogTitle>
            <DialogDescription>
              Choose an image or icon from your assets to use as the kiosk logo
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Upload Button */}
            <div className="flex justify-end">
              <Button onClick={openUploadDialog} size="sm">
                <Upload className="h-4 w-4 mr-2" />
                Upload New Logo
              </Button>
            </div>

            {/* Asset Grid with Selection Handler */}
            <div className="space-y-4">
              <div
                onClick={(e) => {
                  // Handle row click for asset selection
                  const row = (e.target as HTMLElement).closest('tbody tr');
                  if (row && e.target instanceof HTMLElement) {
                    // Ignore clicks on action buttons and interactive elements
                    const isActionClick = e.target.closest('button') ||
                                         e.target.closest('[role="button"]') ||
                                         e.target.closest('a');

                    if (!isActionClick) {
                      // Extract asset index from the table structure
                      const tbody = row.closest('tbody');
                      if (tbody) {
                        const rows = Array.from(tbody.querySelectorAll('tr')) as HTMLTableRowElement[];
                        const rowIndex = rows.indexOf(row as HTMLTableRowElement);

                        if (rowIndex >= 0 && filteredAssets[rowIndex]) {
                          handleSelectAsset(filteredAssets[rowIndex]);
                        }
                      }
                    }
                  }
                }}
              >
                <AssetGrid
                  assets={filteredAssets}
                  isLoading={isLoading}
                  error={error}
                  totalCount={filteredAssets.length}
                  currentPage={currentPage}
                  pageSize={pageSize}
                  onPageChange={handlePageChange}
                  onView={() => {}} // Not used in logo selector
                  onDelete={() => {}} // Not used in logo selector
                  onRetry={handleRetry}
                />
              </div>

              {/* Grid Actions - Select asset on row click */}
              {!isLoading && filteredAssets.length > 0 && (
                <div className="pt-2 space-y-2">
                  <p className="text-xs text-gray-500">
                    Click on an asset row to select it as the logo
                  </p>
                </div>
              )}

              {/* Empty state for no Image/Icon assets */}
              {!isLoading && assets.length > 0 && filteredAssets.length === 0 && (
                <div className="rounded-md border border-[#E8E8E8] bg-gray-50 p-8 text-center">
                  <p className="text-sm text-gray-600">
                    No images or icons found. Upload an image or icon to use as a logo.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Dialog Footer */}
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={handleCloseDialog}>
              Cancel
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Asset Upload Dialog (from asset management) */}
      {uploadDialogOpen && (
        <AssetUploadDialog
          open={uploadDialogOpen}
          onClose={closeUploadDialog}
          onUpload={async (request) => { await uploadAsset(request); }}
          isUploading={isUploading}
          error={uploadError}
          onClearError={clearUploadError}
        />
      )}
    </div>
  );
};
